import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import { Suspense, useState } from "react";
import { getGoldRates } from "@/lib/gold-prices.functions";
import { computeCityPrices, formatMoney } from "@/lib/gold-data";

const searchSchema = z.object({
  country: z.enum(["pakistan", "india"]).default("pakistan"),
  karat: z.enum(["24K", "22K", "21K"]).default("22K"),
  theme: z.enum(["dark", "light"]).default("dark"),
  mode: z.enum(["price", "calculator"]).default("price"),
});

const ratesQO = queryOptions({
  queryKey: ["widget-rates"],
  queryFn: () => getGoldRates(),
  staleTime: 10 * 60 * 1000,
});

export const Route = createFileRoute("/widget")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Live Gold Rate Widget" },
      { name: "robots", content: "noindex" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
    ],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(ratesQO);
  },
  component: WidgetPage,
});

const PURITY = { "24K": 1, "22K": 22 / 24, "21K": 21 / 24 } as const;

function ResponsiveStyles() {
  return (
    <style>{`
      html,body{margin:0;padding:0;background:transparent;box-sizing:border-box;overflow-x:hidden}
      *,*::before,*::after{box-sizing:border-box}
      .dg-widget{width:100%;max-width:100%;box-sizing:border-box;font-family:system-ui,-apple-system,sans-serif}
      .dg-widget input,.dg-widget select{width:100%;max-width:100%;box-sizing:border-box;font-size:14px;padding:8px 10px;border-radius:8px;border:1px solid #2a2a40;background:transparent;color:inherit}
      .dg-row{display:flex;flex-wrap:wrap;gap:8px}
      .dg-grow{flex:1 1 120px;min-width:0}
      .dg-price{word-break:break-word}
      @media (max-width:380px){.dg-price{font-size:26px !important}}
    `}</style>
  );
}

function WidgetPage() {
  return (
    <>
      <ResponsiveStyles />
      <Suspense fallback={<div className="p-6 text-sm">Loading…</div>}>
        <Widget />
      </Suspense>
    </>
  );
}

function Widget() {
  const search = Route.useSearch() as {
    country: "pakistan" | "india";
    karat: "24K" | "22K" | "21K";
    theme: "dark" | "light";
    mode: "price" | "calculator";
  };
  const { country, karat, theme, mode } = search;
  const { data } = useSuspenseQuery(ratesQO);
  const fx = country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
  const prices = computeCityPrices({ spotUsdPerOz: data.spotUsdPerOz, fxRate: fx, country, cityPremium: 1 });
  const main = country === "pakistan" ? prices.perTola24 : prices.per10g24;
  const val = main * PURITY[karat];
  const currency = country === "pakistan" ? "PKR" : "INR";
  const unit = country === "pakistan" ? "per Tola" : "per 10g";

  const dark = theme === "dark";
  const bg = dark ? "#0f1020" : "#fff";
  const fg = dark ? "#fff" : "#111";
  const sub = dark ? "#9ca3af" : "#6b7280";

  if (mode === "calculator") {
    return (
      <CalculatorWidget perGram24={prices.perGram24} currency={currency} country={country} bg={bg} fg={fg} sub={sub} dark={dark} />
    );
  }

  return (
    <div
      className="dg-widget"
      style={{
        background: bg,
        color: fg,
        padding: 16,
        borderRadius: 12,
        border: `1px solid ${dark ? "#D4AF37" : "#e5e7eb"}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: sub, textTransform: "uppercase", letterSpacing: 2 }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: "#ef4444", display: "inline-block" }} />
        LIVE · {country.toUpperCase()}
      </div>
      <div style={{ marginTop: 12, fontSize: 14, color: sub }}>{karat} Gold</div>
      <div className="dg-price" style={{ marginTop: 4, fontSize: 36, fontWeight: 900, color: "#D4AF37", lineHeight: 1.1 }}>
        {formatMoney(val, currency)}
      </div>
      <div style={{ fontSize: 12, color: sub }}>{unit}</div>
      <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${dark ? "#222" : "#eee"}`, fontSize: 11, color: sub }}>
        Powered by{" "}
        <a href="https://drhint.com" target="_blank" rel="noopener" style={{ color: "#D4AF37", fontWeight: 700 }}>
          DrHint
        </a>
      </div>
    </div>
  );
}

function CalculatorWidget({
  perGram24,
  currency,
  country,
  bg,
  fg,
  sub,
  dark,
}: {
  perGram24: number;
  currency: "PKR" | "INR";
  country: "pakistan" | "india";
  bg: string;
  fg: string;
  sub: string;
  dark: boolean;
}) {
  const [weight, setWeight] = useState("10");
  const [karat, setKarat] = useState<"24K" | "22K" | "21K">("22K");
  const w = Number(weight) || 0;
  const value = perGram24 * PURITY[karat] * w;
  return (
    <div
      className="dg-widget"
      style={{
        background: bg,
        color: fg,
        padding: 14,
        borderRadius: 12,
        border: `1px solid ${dark ? "#D4AF37" : "#e5e7eb"}`,
      }}
    >
      <div style={{ fontSize: 11, color: sub, textTransform: "uppercase", letterSpacing: 2, marginBottom: 10 }}>
        Gold Calculator · {country.toUpperCase()}
      </div>
      <div className="dg-row" style={{ marginBottom: 10 }}>
        <div className="dg-grow">
          <label style={{ fontSize: 11, color: sub, display: "block", marginBottom: 4 }}>Weight (g)</label>
          <input type="number" value={weight} onChange={(e) => setWeight(e.target.value)} />
        </div>
        <div className="dg-grow">
          <label style={{ fontSize: 11, color: sub, display: "block", marginBottom: 4 }}>Purity</label>
          <select value={karat} onChange={(e) => setKarat(e.target.value as "24K" | "22K" | "21K")}>
            <option>24K</option>
            <option>22K</option>
            <option>21K</option>
          </select>
        </div>
      </div>
      <div className="dg-price" style={{ fontSize: 28, fontWeight: 900, color: "#D4AF37", lineHeight: 1.15 }}>
        {formatMoney(value, currency)}
      </div>
      <div style={{ fontSize: 11, color: sub, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${dark ? "#222" : "#eee"}` }}>
        Powered by{" "}
        <a href="https://drhint.com" target="_blank" rel="noopener" style={{ color: "#D4AF37", fontWeight: 700 }}>
          DrHint
        </a>
      </div>
    </div>
  );
}
